import { Injectable, signal } from '@angular/core';

/** Section of an element's side sheet that a scan tooltip asked to open. */
export type MarkerOpenSection = 'images' | 'videos' | 'documents';

/**
 * One-shot hand-off between a scan tooltip ("click on photos") and the side sheet it opens:
 * the tooltip sets the section right before `openMarker`, the side sheet consumes it once
 * it has rendered and scrolls to that section.
 */
@Injectable({ providedIn: 'root' })
export class MarkerOpenIntentService {
  private readonly _section = signal<MarkerOpenSection | null>(null);
  readonly section = this._section.asReadonly();

  set(section: MarkerOpenSection | null): void {
    this._section.set(section);
  }

  /** Returns the requested section and clears it. */
  consume(): MarkerOpenSection | null {
    const section = this._section();
    this._section.set(null);
    return section;
  }
}

/**
 * Scrolls `element` into view inside its nearest scrollable ancestor (the side panel body),
 * without moving the page itself.
 */
export function scrollIntoPanel(element: HTMLElement, behavior: ScrollBehavior = 'smooth'): void {
  let container = element.parentElement;
  while (container) {
    const { overflowY } = getComputedStyle(container);
    if ((overflowY === 'auto' || overflowY === 'scroll') && container.scrollHeight > container.clientHeight) break;
    container = container.parentElement;
  }
  if (!container) return;
  const top = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
  container.scrollTo({ top, behavior });
}
